// DESCRIPTION: Two-column wizard shell — content + footer on the left, themed
// decorative panel on the right. Owns the full viewport.

import { __ } from '@wordpress/i18n';
import PreviewPanel from './PreviewPanel';

/**
 * Two-column wizard shell.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.stepKey            Identifier for the preview panel (welcome|persona|identity|hours|notify|payments|finalizing|done).
 * @param {number}   props.progress           0-100. If null, the progress bar is hidden.
 * @param {Function} [props.onBack]
 * @param {Function} [props.onSkip]           Skip only the current step (advance). Rendered as a footer link.
 * @param {Function} [props.onSkipSetup]      Skip the entire setup (finalize + exit). Rendered as a topbar link.
 * @param {Function} [props.onContinue]
 * @param {string}   [props.continueLabel]
 * @param {boolean}  [props.continueDisabled]
 * @param {boolean}  [props.isLoading]
 * @param {boolean}  [props.hideFooter]
 * @param {Node}     props.children
 *
 * @return {JSX.Element}
 */
export default function WizardShell( {
	stepKey,
	progress,
	onBack,
	onSkip,
	onSkipSetup,
	onContinue,
	continueLabel,
	continueDisabled,
	isLoading,
	hideFooter,
	children,
} ) {
	const showProgress = typeof progress === 'number';

	return (
		<div className="nemtly-wizard-page">
			{ /* LEFT — content column */ }
			<div className="nemtly-wizard-left">
				<div className="nemtly-wizard-topbar">
					<span className="nemtly-wizard-brand">
						{ __( 'Nemtly Booking', 'nemtly-booking' ) }
					</span>

					{ onSkipSetup && (
						<button
							type="button"
							className="nemtly-wizard-btn nemtly-wizard-btn--link nemtly-wizard-skip-setup"
							onClick={ onSkipSetup }
							disabled={ isLoading }
						>
							{ __( 'Skip setup', 'nemtly-booking' ) } →
						</button>
					) }
				</div>

				{ showProgress && (
					<div className="nemtly-wizard-progress-wrap">
						<div className="nemtly-wizard-progress">
							<div
								className="nemtly-wizard-progress-fill"
								style={ {
									width: `${ Math.max(
										0,
										Math.min( 100, progress )
									) }%`,
								} }
								aria-hidden="true"
							/>
						</div>
					</div>
				) }

				<div className="nemtly-wizard-content">{ children }</div>

				{ ! hideFooter && (
					<footer className="nemtly-wizard-footer">
						<div className="nemtly-wizard-footer-left">
							{ onBack && (
								<button
									type="button"
									className="nemtly-wizard-btn nemtly-wizard-btn--secondary"
									onClick={ onBack }
									disabled={ isLoading }
								>
									← { __( 'Back', 'nemtly-booking' ) }
								</button>
							) }
						</div>

						<div className="nemtly-wizard-footer-center">
							{ onSkip && (
								<button
									type="button"
									className="nemtly-wizard-btn nemtly-wizard-btn--link"
									onClick={ onSkip }
									disabled={ isLoading }
								>
									{ __( 'Skip this step', 'nemtly-booking' ) }
								</button>
							) }
						</div>

						<div className="nemtly-wizard-footer-right">
							{ onContinue && (
								<button
									type="button"
									className="nemtly-wizard-btn nemtly-wizard-btn--primary"
									onClick={ onContinue }
									disabled={ isLoading || continueDisabled }
								>
									{ isLoading
										? __( 'Working…', 'nemtly-booking' )
										: continueLabel ||
										  __( 'Continue', 'nemtly-booking' ) }
								</button>
							) }
						</div>
					</footer>
				) }
			</div>

			{ /* RIGHT — decorative panel */ }
			<div className="nemtly-wizard-right">
				<PreviewPanel stepKey={ stepKey } />
			</div>
		</div>
	);
}
